/**
 * paystackService.js
 * Handles Paystack inline popup on the frontend.
 * Loads the Paystack inline script dynamically — no npm package needed.
 */

const PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

// Dynamically load Paystack inline script
function loadPaystackScript() {
  return new Promise((resolve, reject) => {
    if (window.PaystackPop) return resolve(window.PaystackPop);
    const script = document.createElement("script");
    script.src = "https://js.paystack.co/v1/inline.js";
    script.onload = () => resolve(window.PaystackPop);
    script.onerror = () => reject(new Error("Failed to load Paystack script"));
    document.head.appendChild(script);
  });
}

/**
 * Open Paystack inline popup.
 * @param {object} opts
 * @param {string}   opts.email
 * @param {number}   opts.amount       — in GHS (we convert to pesewas)
 * @param {string}   opts.reference
 * @param {string}   opts.access_code  — from backend /payments/initialize
 * @param {string}   opts.payment_method — card | mobile_money | ussd | bank_transfer
 * @param {function} opts.onSuccess(reference) — called when payment succeeds
 * @param {function} opts.onClose()            — called when popup is closed without paying
 */
export async function openPaystackPopup({ email, amount, reference, access_code, payment_method, onSuccess, onClose }) {
  const PaystackPop = await loadPaystackScript();

  // Map our payment_method to Paystack channels
  const channelMap = {
    card:          ["card"],
    mobile_money:  ["mobile_money"],
    ussd:          ["ussd"],
    bank_transfer: ["bank_transfer"],
  };
  const channels = channelMap[payment_method] || ["card", "mobile_money"];

  const handler = PaystackPop.setup({
    key:        PUBLIC_KEY,
    email,
    amount:     Math.round(amount * 100), // pesewas
    currency:   "GHS",
    ref:        reference,
    access_code,                          // pre-initialised transaction
    channels,
    label:      "KwiKie Delivery",
    metadata: {
      custom_fields: [
        { display_name: "Platform", variable_name: "platform", value: "KwiKie Delivery" },
      ],
    },
    onClose() {
      if (onClose) onClose();
    },
    callback(response) {
      if (onSuccess) onSuccess(response.reference || response.trxref);
    },
  });

  handler.openIframe();
}

/**
 * Poll backend until payment_status = paid (max 2 minutes, every 3s).
 * Used as fallback in case webhook fires but frontend missed it.
 */
export async function pollPaymentStatus(orderId, token, onPaid, onFailed) {
  const API = process.env.REACT_APP_API_URL;
  let attempts = 0;
  const maxAttempts = 40; // 40 × 3s = 2 minutes

  const interval = setInterval(async () => {
    attempts++;
    try {
      const res  = await fetch(`${API}/payments/status/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const { payment_status, status } = data.data || {};

      if (payment_status === "paid") {
        clearInterval(interval);
        if (onPaid) onPaid(status);
      } else if (payment_status === "failed" || status === "cancelled") {
        clearInterval(interval);
        if (onFailed) onFailed();
      }
    } catch {}

    if (attempts >= maxAttempts) {
      clearInterval(interval);
      if (onFailed) onFailed("timeout");
    }
  }, 3000);

  return () => clearInterval(interval); // returns cleanup function
}
